import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { Box, IconButton, Tooltip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import axiosHttp from "../../../../../services/interceptor";
import { callApi } from "../../../../../services/MonitoringPlanService";
import { API_ROUTES } from "../../FeuApiRoutes/FeuApiRoutes";
import { emitToasterMsg } from "../../../../../services/toasterUtility";
import { useParams } from "react-router-dom";
import { useFormContext } from "react-hook-form";
 
const FeuC15UncertainityLevel = forwardRef(
    ({ c27LevelOfUncertainity, refetchMonitoringData, shouldRefetch, levelOfUncertainities, monitoringMethod, vesselMstId }, ref) => {
        const RowActions = {
            Edit: 0,
            Add: 1,
        };
        const [activeAction, setactiveAction] = useState(-1);
        const [levelData, setLevelData] = useState([]);
        const [validationErrors, setValidationErrors] = useState(c27LevelOfUncertainity || []);
        const [hasFetched, setHasFetched] = useState(false);
 
       
 
        const getC15data = async () => {
            const url = `api/FuelEuMonitoringPlan/partc/GetC15DataByVesselId?vesselMstId=${vesselMstId}`;
            try {
                const response = await axiosHttp({ url, method: "GET" });
                if (response?.status === 200 && response.data) {
                    const normalized = response.data.map((row) => ({
                        ...row,
                        value: row.value ?? null,
                    }));
                    setLevelData(normalized);
                }
            } catch (error) {
                console.error("Error fetching C15 data", error);
            }
        };
 
        useEffect(() => {
            if (shouldRefetch && !hasFetched) {
                getC15data();
                setHasFetched(true);
            }
        }, [shouldRefetch]);
 
        useEffect(() => {
            if (!shouldRefetch) {
                setHasFetched(false);
            }
        }, [shouldRefetch]);
 
        const getApproachesUsed = () => {
            return levelOfUncertainities?.map((item) => ({
                label: item.value,
                value: item.value,
            }));
        };
 
        const handleFuelActions = async (table, row, action) => {
            if (activeAction === -1) {
                if (action === RowActions.Edit) {
                    table.setEditingRow(row);
                    setactiveAction(action);
                } else if (action === RowActions.Add) {
                    table.setCreatingRow(true);
                    setactiveAction(action);
                }
            }
        };
 
        const resetActiveRow = () => {
            setactiveAction(-1);
            setValidationErrors({});
        };
 
        useImperativeHandle(ref, () => ({
            async submitAndCheck() {
                const { creatingRow, editingRow } = table.getState();
                if (!creatingRow && !editingRow) return true;
                const mode = creatingRow ? "add" : "edit";
                const editedValues = creatingRow ? creatingRow?._valuesCache : editingRow?._valuesCache;
                let isDirty = false;
                if (editingRow && editedValues) {
                    isDirty = Object.keys(editedValues).some((key) => editedValues[key] !== editingRow.original[key]);
                }
                const validationRes = {};
                const hasErrors = Object.values(validationRes).some((err) => err);
 
                if (hasErrors) {
                    setValidationErrors(validationRes);
                    emitToasterMsg("warning", "Validation Error", "Please correct the highlighted fields before proceeding.");
                    return false;
                }
 
                // Autosave on valid & dirty
                if (editingRow && isDirty) {
                    await handleSaveLevel({ values: editedValues, table, row: editingRow }, true);
                    return true;
                }
                if (!isDirty) {
                    setactiveAction(-1);
                    table.setEditingRow(null);
                    return true;
                }
                return true;
            },
        }));
 
        const handleSaveLevel = async ({ values, table, row }, isAuto = false) => {
            if (values.approachUsed && !values.value?.toString().trim()) {
                setValidationErrors({
                    value: "field is required",
                });
                return;
            }
 
            const updatedData = levelData?.map((item, index) => {
                if (index === row.index) {
                    return {
                        ...item,
                        emissionSourceRefNo: values.emissionSourceRefNo || "",
                        monitoringMethod: values.monitoringMethod || [],
                        emissionGuid: item.emissionGuid || "",
                        approachUsed: values.approachUsed || null,
                        value: values.value ?? null,
                    };
                }
                return item;
            });
            const finalPayload = updatedData?.map((item) => ({
                ...item,
                c27LevelOfUncertainityId: 0,
            }));
 
            try {
                const c27response = await callApi({
                    method: "POST",
                    url: API_ROUTES.C15(vesselMstId),
                    data: finalPayload,
                });
 
                if (c27response?.isSuccess) {
                    emitToasterMsg("success", "Table C.1.5", isAuto ? "Level has been autosaved successfully" : "Level has been Saved successfully");
                    // emitToasterMsg("success", "C15", "Level has been Saved successfully");
                    table.setEditingRow(null);
                    setactiveAction(-1);
                    if (typeof refetchMonitoringData === "function") {
                        await refetchMonitoringData();
                    }
                    getC15data();
                } else {
                    emitToasterMsg("error", "C.1.5", c27response?.errorMessage || "Save failed");
                }
            } catch (error) {
                emitToasterMsg("error", "C.1.5", error.errorMessage);
            }
        };
 
        const columns = useMemo(
            () => [
                {
                    accessorKey: "emissionSourceRefNo",
                    header: "Fuel consumer reference no",
                    enableEditing: false,
                },
                {
                    accessorKey: "monitoringMethod",
                    header: "Monitoring Method",
                    enableEditing: false,
                    Cell: ({ cell }) => {
                        const codes = cell.getValue();
                        if (!Array.isArray(codes)) return "";
 
                        return codes
                            .map((code) => {
                                const matched = monitoringMethod.find((opt) => opt.code === code);
                                return matched ? matched.name : code;
                            })
                            .join(", ");
                    },
                },
                {
                    accessorKey: "approachUsed",
                    header: "Approach used",
                    editVariant: "select",
                    editSelectOptions: getApproachesUsed(),
                    muiEditTextFieldProps: ({ row }) => {
                        const monitoringMethod = row.original.monitoringMethod || [];
                        const multipleMonitoringMethods = monitoringMethod.length > 1;
 
                        if (multipleMonitoringMethods) {
                            row._valuesCache.approachUsed = "Ship specific estimate";
                        }
 
                        return {
                            select: true,
                            required: false,
                            value: row._valuesCache.approachUsed || "",
                            error: !!validationErrors?.approachUsed,
                            helperText: validationErrors?.approachUsed,
                            disabled: multipleMonitoringMethods,
                            onChange: (e) => {
                                const selectedValue = e.target.value;
                                row._valuesCache.approachUsed = selectedValue;
 
                                if (selectedValue === "Default value") {
                                    row._valuesCache.value = "7.5";
                                } else {
                                    row._valuesCache.value = "";
                                }
 
                                setValidationErrors((prev) => ({
                                    ...prev,
                                    approachUsed: undefined,
                                }));
                            },
                        };
                    },
                },
                {
                    accessorKey: "value",
                    header: "Value of uncertainty",
                    muiEditTextFieldProps: ({ row }) => {
                        const approachUsed = row._valuesCache?.approachUsed;
                        const monitoringMethod = row._valuesCache?.monitoringMethod || [];
                        const shouldDisable = !approachUsed || approachUsed === "Default value";
                        const value = approachUsed === "Default value" && monitoringMethod.length > 0 ? "7.5" : row._valuesCache?.value ?? "";
                        return {
                            required: true,
                            value: value,
                            type: "number",
                            disabled: shouldDisable,
                            error: !!validationErrors?.value,
                            helperText: validationErrors?.value,
                            onFocus: () =>
                                setValidationErrors((prev) => ({
                                    ...prev,
                                    value: undefined,
                                })),
                            onChange: (e) => {
                                const inputVal = e.target.value;
 
                                if (inputVal === "") {
                                    row._valuesCache.value = null;
                                } else if (!/^\d*\.?\d*$/.test(inputVal)) {
                                    setValidationErrors((prev) => ({
                                        ...prev,
                                        value: "Please enter a number",
                                    }));
                                } else {
                                    setValidationErrors((prev) => ({
                                        ...prev,
                                        value: undefined,
                                    }));                                
                                    row._valuesCache.value = Number(inputVal);
                                }
                            },
                        };
                    },
                },
            ],
            [validationErrors]
        );
 
        const table = useMaterialReactTable({
            columns,
            data: levelData,
            enableStickyHeader: true,
            enableSorting: false,
            enableRowSelection: false,
            enableColumnPinning: false,
            enableTopToolbar: false,
            enableBottomToolbar: false,
            enableColumnActions: false,
            createDisplayMode: "row",
            editDisplayMode: "row",
            enableEditing: true,
            enablePagination: false,
            muiTableHeadCellProps: {
                sx: {
                    color: "rgb(255 255 255)",
                    backgroundColor: "#042a4c",
                    position: "sticky",
                    borderRight: "1px solid lightgray",
                    "&:last-child": {
                        borderRight: "none",
                    },
                },
            },
            muiTablePaperProps: {
                sx: {
                    boxShadow: "0 0 8px 2px rgba(0, 0, 0, 0.1)",
                },
            },
            muiTableContainerProps: {
                sx: {
                    maxHeight: "520px",
                },
            },
            displayColumnDefOptions: {
                "mrt-row-actions": {
                    muiTableHeadCellProps: {
                        sx: {
                            borderBottom: "none",
                            borderRight: "1px solid lightgray",
                        },
                    },
                },
            },
            muiTableBodyCellProps: () => ({
                sx: {
                    borderRight: "1px solid lightgray",
                    "&:last-child": {
                        borderRight: "none",
                    },
                },
            }),
            muiTableBodyRowProps: {
                sx: {
                    "&:not(:last-of-type)": {
                        borderBottom: "1px solid lightgray",
                    },
                    verticalAlign: "top",
                },
            },
            muiTableHeadRowProps: {
                sx: {
                    borderBottom: "2px solid lightgray",
                },
            },
            getRowId: (row) => row.emissionSourceRefNo,
            onEditingRowCancel: resetActiveRow,
            onCreatingRowCancel: resetActiveRow,
            onEditingRowSave: handleSaveLevel,
            renderToolbarInternalActions: () => <></>,
            renderRowActions: ({ row, table }) => (
                <Box sx={{ display: "flex", gap: "1rem" }}>
                    <Tooltip title="Edit">
                        <IconButton style={{ padding: "0px" }} onClick={() => handleFuelActions(table, row, RowActions.Edit)}>
                            <EditIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            ),
        });
 
        return (
            <div className="c27-uncertainitylevel-table mt-1">
                <MaterialReactTable table={table} />
            </div>
        );
    }
);
 
export default FeuC15UncertainityLevel;